import React, { Component, type ReactNode } from 'react';
import { Text, StyleSheet, ScrollView } from 'react-native';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: string | null;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    console.log('ErrorBoundary: getDerivedStateFromError called');
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error', error, errorInfo);
    this.setState({ errorInfo: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError) {
      console.log('ErrorBoundary: Rendering fallback UI');
      return (
        <ScrollView contentContainerStyle={styles.container}>
          <Text style={styles.errorText}>Something went wrong:</Text>
          <Text style={styles.errorDetails}>{this.state.error?.message}</Text>
          {this.state.errorInfo && (
            <Text style={styles.errorStack}>{this.state.errorInfo}</Text>
          )}
        </ScrollView>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f8d7da',
  },
  errorText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#721c24',
    marginBottom: 8,
  },
  errorDetails: {
    fontSize: 14,
    color: '#721c24',
    marginBottom: 16,
  },
  errorStack: {
    fontSize: 12,
    color: '#555',
    marginTop: 16,
    textAlign: 'left',
  },
});
